import { type FC } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faArrowLeft,
  faCode,
  faSlidersH,
  faFlask,
} from '@fortawesome/free-solid-svg-icons';
import PublicNavBar from '../../components/layout/PublicNavBar';
import PipelineStrip from '../../components/layout/PipelineStrip';
import PublicFooter from '../../components/layout/PublicFooter';

import IDEPreviewHero, { S, cm, kw, fn, pr, st, nm, strategyCode } from './workspace/IDEPreviewHero';
import { CodePanel, ConsolePanel } from './workspace/CodePanel';
import VersionTimelineMock from './workspace/VersionTimelineMock';
import { usePageMeta } from '../../hooks/usePageMeta';

/* ═══════════════════════════ PAGE ═════════════════════════════════════ */

const WorkspacePage: FC = () => {
  usePageMeta({ title: 'Research Workspace', description: 'A code-first environment for systematic strategy research. Write QSL, run backtests, and manage strategy versions in one structured workspace.' });

  return (
    <div className="min-h-screen bg-[#0a0e14] text-gray-100 font-[Poppins] selection:bg-[#9d4edd]/30">
      <PublicNavBar />

      {/* ════════════════ 1. HERO ════════════════ */}
      <section className="relative pt-24 sm:pt-36 pb-28 px-4 sm:px-6 lg:px-10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0f1520] via-[#0a0e14] to-[#080b11]" />
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.15) 1px, transparent 1px)', backgroundSize: '24px 24px' }} />

        <div className="max-w-[1440px] mx-auto relative z-10 grid grid-cols-1 lg:grid-cols-[5fr_7fr] gap-12 lg:gap-16 items-center">
          {/* Left – copy */}
          <div>
            <p className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#9d4edd]/60 mb-5">
              01 &mdash; Research Workspace
            </p>

            <h1 className="text-3xl sm:text-[2.25rem] md:text-[2.5rem] font-bold leading-[1.1] tracking-[-0.025em] mb-4 text-white">
              A structured workspace for systematic strategy research
            </h1>

            <p className="text-[15px] text-gray-300/70 leading-[1.7] mb-5 max-w-[500px]">
              A code-first environment where strategy logic, parameters,
              and validation workflows operate inside the same research system.
            </p>

            <div className="space-y-2 mb-7">
              {[
                <>Define strategy logic in <span className="text-white font-medium">QSL</span></>,
                'Control parameters and optimization ranges explicitly',
                <>Run <span className="text-white font-medium">Baseline &rarr; Optimization &rarr; WFA &rarr; Adaptive Flow</span> without leaving the workspace</>,
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <span className="w-1 h-1 rounded-full bg-[#9d4edd]/50 mt-[7px] flex-shrink-0" />
                  <span className="text-[13px] text-gray-400 leading-snug">{item}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-4">
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2.5 px-7 py-3 bg-[#9d4edd] hover:bg-[#8b3ec7] text-white rounded text-sm font-medium transition-colors"
              >
                Start Research
                <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
              </Link>
              <a href="#workspace-overview" className="text-[13px] text-gray-400 hover:text-gray-200 transition-colors">
                View Workspace Overview <span className="text-gray-600">&rarr;</span>
              </a>
            </div>
          </div>

          {/* Right – IDE workspace preview */}
          <IDEPreviewHero />
        </div>
      </section>

      <PipelineStrip active="workspace" />

      {/* ════════════════ 2. WORKSPACE OVERVIEW ════════════════ */}
      <section id="workspace-overview" className="py-16 px-4 sm:px-6 lg:px-10 bg-[#F9FAFB]">
        <div className="max-w-[960px] mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-xl md:text-[24px] font-bold text-gray-950 tracking-[-0.015em] mb-5">
              A research IDE for systematic trading
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.7] max-w-[560px] mx-auto">
              Quanthop provides a structured workspace for developing and
              validating systematic trading strategies. Strategy logic, parameter
              definitions, and validation workflows operate inside the same
              research environment.
            </p>
          </div>

          {/* 3 pillars */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: faCode,
                title: 'Strategy Editor',
                text: 'Write strategy logic in QSL with explicit parameter definitions.',
              },
              {
                icon: faSlidersH,
                title: 'Parameter System',
                text: 'Define parameter ranges, defaults, and optimization behavior directly inside the strategy.',
              },
              {
                icon: faFlask,
                title: 'Validation Engine',
                text: 'Run Baseline, Optimization, Walk-Forward, and Adaptive Flow from the same workspace.',
              },
            ].map((p) => (
              <div
                key={p.title}
                className="rounded-lg border border-gray-200 bg-white px-5 py-6"
              >
                <div className="w-8 h-8 rounded-md bg-[#9d4edd]/10 flex items-center justify-center mb-4">
                  <FontAwesomeIcon icon={p.icon} className="text-[#9d4edd] text-sm" />
                </div>
                <h3 className="text-[14px] font-semibold text-gray-950 mb-2">{p.title}</h3>
                <p className="text-[13px] text-gray-500 leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════ 3. CODE-FIRST DEVELOPMENT ════════════════ */}
      <section className="py-16 px-4 sm:px-6 lg:px-10 bg-[#F0F2F5]">
        <div className="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-[5fr_7fr] gap-12 lg:gap-16 items-center">
          <div>
            <p className="text-[10px] font-mono tracking-[0.15em] uppercase text-[#9d4edd]/40 mb-2">
              QSL Strategy Language
            </p>
            <h2 className="text-xl md:text-[22px] font-bold text-gray-950 tracking-[-0.015em] mb-5">
              Code-first strategy development with QSL
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.7] mb-4">
              Strategies in Quanthop are written using <span className="text-gray-900 font-medium">QSL</span>, a
              structured language designed for reproducible strategy research.
            </p>
            <p className="text-[15px] text-gray-600 leading-[1.7] mb-6">
              QSL is intentionally constrained to ensure strategies behave
              deterministically during validation.
            </p>

            <p className="text-[13px] text-gray-500 mb-3 font-medium">Key characteristics</p>
            <div className="space-y-2 mb-6">
              {[
                'Explicit parameter definitions',
                'Deterministic strategy execution',
                'Built-in indicator functions',
                'Direct integration with the validation pipeline',
              ].map((item) => (
                <div key={item} className="flex items-start gap-2.5">
                  <span className="w-1 h-1 rounded-full bg-gray-400 mt-[7px] flex-shrink-0" />
                  <span className="text-[13px] text-gray-700 leading-snug">{item}</span>
                </div>
              ))}
            </div>
          </div>

          <CodePanel
            fileName="strategy.qsl"
            lines={strategyCode}
            highlightSet={new Set([5, 6, 7, 8, 9, 10, 11, 12, 13, 15, 16, 17, 18, 19, 20, 21, 22, 23])}
            caption="Parameters defined in the strategy automatically appear in optimization and walk-forward validation stages."
          />
        </div>
      </section>

      {/* ════════════════ 4. DETERMINISTIC ENGINE ════════════════ */}
      <section className="py-16 px-4 sm:px-6 lg:px-10 bg-white">
        <div className="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-12 lg:gap-18 items-center">
          <ConsolePanel
            title="Backtest"
            context="BTCUSDT · 1D · ema_cross v0.3"
            lines={[
              { time: '14:32:01', type: 'info', source: 'engine', message: 'Starting backtest run #48' },
              { time: '14:32:01', type: 'info', source: 'engine', message: 'Run hash: a7f2c91' },
              { time: '14:32:04', type: 'success', source: 'engine', message: 'Execution complete — 142 trades' },
              { time: '14:32:04', type: 'info', source: 'engine', message: 'Previous hash: a7f2c91' },
              { time: '14:32:04', type: 'success', source: 'verify', message: 'Deterministic — identical to run #47' },
              { time: '14:32:04', type: 'success', source: 'result', message: 'Net return: +34.7%' },
            ]}
          />

          <div>
            <p className="text-[10px] font-mono tracking-[0.15em] uppercase text-[#9d4edd]/40 mb-2">
              Execution Model
            </p>
            <h2 className="text-xl md:text-[22px] font-bold text-gray-950 tracking-[-0.015em] mb-5">
              Deterministic execution model
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.7] mb-2">
              Strategy behaviour must be predictable.
            </p>
            <p className="text-[15px] text-gray-600 leading-[1.7] mb-6">
              Quanthop executes strategies inside a deterministic simulation
              engine where identical inputs always produce identical outputs.
            </p>

            <p className="text-[13px] text-gray-500 mb-3 font-medium">This ensures</p>
            <div className="space-y-2 mb-8">
              {[
                'Reproducible optimization results',
                'Reliable walk-forward validation',
                'Consistent research comparisons across strategy versions',
              ].map((item) => (
                <div key={item} className="flex items-start gap-2.5">
                  <span className="w-1 h-1 rounded-full bg-gray-400 mt-[7px] flex-shrink-0" />
                  <span className="text-[13px] text-gray-700 leading-snug">{item}</span>
                </div>
              ))}
            </div>

            <p className="text-[14px] text-gray-900 leading-[1.7] font-medium">
              Running the same strategy with the same parameters and data
              will always produce the same result.
            </p>
          </div>
        </div>
      </section>

      {/* ════════════════ 5. PARAMETER BOUNDARIES ════════════════ */}
      <section className="py-16 px-4 sm:px-6 lg:px-10 bg-[#F9FAFB]">
        <div className="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <p className="text-[10px] font-mono tracking-[0.15em] uppercase text-[#9d4edd]/40 mb-2">
              Parameter Control
            </p>
            <h2 className="text-xl md:text-[22px] font-bold text-gray-950 tracking-[-0.015em] mb-5">
              Parameters are part of the research model
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.7] mb-4">
              Parameters in Quanthop are defined explicitly as part of the
              strategy.
            </p>

            <p className="text-[13px] text-gray-500 mb-3 font-medium">Each parameter definition includes:</p>
            <div className="space-y-2 mb-6">
              {[
                'Minimum and maximum bounds',
                'Default value',
                'Step resolution',
                'Optimization flag',
              ].map((item) => (
                <div key={item} className="flex items-start gap-2.5">
                  <span className="w-1 h-1 rounded-full bg-gray-400 mt-[7px] flex-shrink-0" />
                  <span className="text-[13px] text-gray-700 leading-snug">{item}</span>
                </div>
              ))}
            </div>

            <p className="text-[14px] text-gray-900 leading-[1.7] font-medium">
              These constraints ensure optimization explores meaningful regions
              of parameter space rather than arbitrary values.
            </p>
          </div>

          <CodePanel
            fileName="strategy.qsl"
            lines={[
              <><S c={pr}>ctx</S>.<S c={fn}>param</S>(<S c={st}>&apos;slowLength&apos;</S>, {'{'}</>,
              <>&nbsp;&nbsp;<S c={pr}>type</S>: <S c={st}>&apos;int&apos;</S>,</>,
              <>&nbsp;&nbsp;<S c={pr}>label</S>: <S c={st}>&apos;Slow EMA Length&apos;</S>,</>,
              <>&nbsp;&nbsp;<S c={pr}>default</S>: <S c={nm}>21</S>,</>,
              <>&nbsp;&nbsp;<S c={pr}>min</S>: <S c={nm}>10</S>,&nbsp;&nbsp;&nbsp;&nbsp;<S c={cm}>{'// lower bound'}</S></>,
              <>&nbsp;&nbsp;<S c={pr}>max</S>: <S c={nm}>200</S>,&nbsp;&nbsp;&nbsp;<S c={cm}>{'// upper bound'}</S></>,
              <>&nbsp;&nbsp;<S c={pr}>step</S>: <S c={nm}>10</S>,&nbsp;&nbsp;&nbsp;<S c={cm}>{'// grid resolution'}</S></>,
              <>&nbsp;&nbsp;<S c={pr}>optimize</S>: <S c={kw}>true</S>,</>,
              <>{'}'});</>,
            ]}
            highlightSet={new Set([4, 5, 6])}
            caption="The min, max, and step values define the optimization search space."
          />
        </div>
      </section>

      {/* ════════════════ 6. VERSIONED ITERATIONS ════════════════ */}
      <section className="py-16 px-4 sm:px-6 lg:px-10 bg-[#F0F2F5]">
        <div className="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-[5fr_7fr] gap-12 lg:gap-16 items-center">
          <VersionTimelineMock />

          <div>
            <p className="text-[10px] font-mono tracking-[0.15em] uppercase text-[#9d4edd]/40 mb-2">
              Research Tracking
            </p>
            <h2 className="text-xl md:text-[22px] font-bold text-gray-950 tracking-[-0.015em] mb-5">
              Every strategy iteration is versioned
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.7] mb-4">
              Strategy development is iterative. Each modification to a
              strategy creates a new research version within the workspace.
            </p>

            <p className="text-[13px] text-gray-500 mb-3 font-medium">Research versions allow you to:</p>
            <div className="space-y-2 mb-8">
              {[
                'Compare strategy iterations',
                'Reproduce previous experiments',
                'Maintain a complete research audit trail',
              ].map((item) => (
                <div key={item} className="flex items-start gap-2.5">
                  <span className="w-1 h-1 rounded-full bg-gray-400 mt-[7px] flex-shrink-0" />
                  <span className="text-[13px] text-gray-700 leading-snug">{item}</span>
                </div>
              ))}
            </div>

            <p className="text-[14px] text-gray-900 leading-[1.7] font-medium">
              This creates a structured record of strategy evolution over time.
            </p>
          </div>
        </div>
      </section>

      {/* ════════════════ 7. VALIDATION WORKFLOW ════════════════ */}
      <section className="py-16 px-4 sm:px-6 lg:px-10 bg-white">
        <div className="max-w-[960px] mx-auto">
          <div className="text-center mb-14">
            <p className="text-[10px] font-mono tracking-[0.15em] uppercase text-[#9d4edd]/40 mb-2">
              Validation Pipeline
            </p>
            <h2 className="text-xl md:text-[22px] font-bold text-gray-950 tracking-[-0.015em] mb-5">
              Validation runs directly inside the workspace
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.7] max-w-[560px] mx-auto">
              Once a strategy is defined, it flows directly into the Quanthop
              validation pipeline. Every stage of research runs inside the same
              workspace.
            </p>
          </div>

          {/* 4 validation stages */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8 relative">
            {/* Arrow connectors between cards (visible on large screens) */}
            <div className="hidden lg:flex absolute inset-0 items-center justify-between pointer-events-none z-10 px-[1px]">
              {[1, 2, 3].map((i) => (
                <div key={i} className="absolute" style={{ left: `${i * 25}%`, transform: 'translateX(-50%)' }}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="text-gray-400/50">
                    <path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              ))}
            </div>
            {[
              { label: 'Baseline Validation', file: 'baseline_report', desc: 'Evaluate strategy behaviour across multiple assets.', result: 'PASS', resultColor: '#a6e3a1' },
              { label: 'Parameter Optimization', file: 'optimization.log', desc: 'Explore parameter combinations within defined bounds.', result: 'Stable', resultColor: '#a6e3a1' },
              { label: 'Walk-Forward Analysis', file: 'wfa_results', desc: 'Test strategy stability across rolling in-sample and out-of-sample windows.', result: 'Stability: 0.63', resultColor: '#89b4fa' },
              { label: 'Adaptive Flow', file: 'adaptive.log', desc: 'Monitor strategy behavior and trigger re-optimization when required.', result: 'Active', resultColor: '#a6e3a1' },
            ].map((stage) => (
              <div
                key={stage.label}
                className="rounded-lg border border-gray-200/80 overflow-hidden shadow-lg shadow-black/10"
                style={{ background: '#1e1e2e' }}
              >
                <div className="px-3 py-1.5 border-b border-white/[0.06] font-mono" style={{ background: '#181825' }}>
                  <span className="text-[10px] text-gray-500">{stage.file}</span>
                </div>
                <div className="px-3 py-3">
                  <h3 className="text-[11px] font-semibold text-gray-300 mb-1.5">{stage.label}</h3>
                  <p className="text-[10px] text-gray-500 leading-relaxed mb-3">{stage.desc}</p>
                  <div className="flex items-center gap-1.5 font-mono">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: stage.resultColor }} />
                    <span className="text-[10px]" style={{ color: stage.resultColor }}>{stage.result}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════ 8. CTA ════════════════ */}
      <section className="relative py-28 px-4 sm:px-6 lg:px-10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-[#9d4edd]/[0.03] to-transparent pointer-events-none" />
        <div className="max-w-[600px] mx-auto text-center relative z-10">
          <h2 className="text-xl md:text-[24px] font-bold text-white tracking-[-0.02em] mb-5">
            Start building structured strategies
          </h2>
          <p className="text-[15px] text-gray-300/70 leading-[1.7] mb-10">
            Quanthop provides a research environment designed for disciplined
            strategy development. Secure access to begin building and validating
            strategies.
          </p>
          <Link
            to="/pricing"
            className="inline-flex items-center gap-2.5 px-7 py-3.5 bg-[#9d4edd] hover:bg-[#8b3ec7] text-white rounded text-sm font-medium transition-colors"
          >
            Start Research
            <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
          </Link>
        </div>
      </section>

      {/* ════════════════ NAV FOOTER ════════════════ */}
      <section className="py-12 px-4 sm:px-6 lg:px-10 border-t border-gray-700/40">
        <div className="max-w-[960px] mx-auto flex items-center justify-between">
          <Link to="/features/research-pipeline" className="inline-flex items-center gap-2 text-[13px] text-gray-500 hover:text-gray-300 transition-colors">
            <FontAwesomeIcon icon={faArrowLeft} className="text-[10px]" />
            Research Pipeline
          </Link>
          <Link to="/features/validation" className="inline-flex items-center gap-2 text-[13px] text-[#9d4edd] hover:text-[#b266ff] transition-colors font-medium">
            Next: Structured Validation
            <FontAwesomeIcon icon={faArrowRight} className="text-[11px]" />
          </Link>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default WorkspacePage;
